import { badgeVariants } from "@volca/ui/components/ui/badge";
import { PageTabsList } from "@volca/ui/components/ui/page-tabs-list";
import { Tabs, TabsTrigger } from "@volca/ui/components/ui/tabs";
import { AttentionCount } from "@/app-shell";
import {
  ST_ADJUSTMENTS_ACCESS,
  type StAdjustmentsAccess,
} from "./st-adjustments/types";
import {
  PAYROLL_EMAILS_ACCESS,
  PAY_BREAKDOWN_SECTION,
  type PayBreakdownSection,
  type PayrollEmailsAccess,
} from "./types";

// Inline after the label, like AP's review count and the sidebar's "New".
const newTabBadgeClassName = badgeVariants({
  variant: "outline",
  size: "sm",
  className: "text-ink/70",
});

interface PayBreakdownSectionTabsProps {
  activeSection: PayBreakdownSection;
  showRoster: boolean;
  showPayPlan: boolean;
  payrollEmailsAccess: PayrollEmailsAccess;
  /** Open message threads in this period, counted on PackChat. */
  openThreadCount?: number;
  showPackChat?: boolean;
  /** PushPay access controls whether the tab is hidden, live, or a preview. */
  stAdjustmentsAccess: StAdjustmentsAccess;
  onSectionChange: (section: PayBreakdownSection) => void;
}

export const PayBreakdownSectionTabs = ({
  activeSection,
  showRoster,
  showPayPlan,
  payrollEmailsAccess,
  openThreadCount = 0,
  showPackChat = false,
  stAdjustmentsAccess,
  onSectionChange,
}: PayBreakdownSectionTabsProps) => (
  <Tabs
    value={activeSection}
    onValueChange={(value) => onSectionChange(value as PayBreakdownSection)}
    className="w-full"
  >
    <PageTabsList>
      <TabsTrigger value={PAY_BREAKDOWN_SECTION.breakdown}>Grid</TabsTrigger>
      {showRoster && (
        <TabsTrigger value={PAY_BREAKDOWN_SECTION.roster}>Roster</TabsTrigger>
      )}
      {showPayPlan && (
        <TabsTrigger value={PAY_BREAKDOWN_SECTION.payPlan}>Plan</TabsTrigger>
      )}
      {stAdjustmentsAccess !== ST_ADJUSTMENTS_ACCESS.hidden && (
        <TabsTrigger
          value={PAY_BREAKDOWN_SECTION.adjustments}
          aria-label={
            stAdjustmentsAccess === ST_ADJUSTMENTS_ACCESS.preview
              ? "PushPay, new"
              : undefined
          }
        >
          <span className="inline-flex items-center gap-1.5">
            PushPay
            {stAdjustmentsAccess === ST_ADJUSTMENTS_ACCESS.preview && (
              <span className={newTabBadgeClassName} aria-hidden>
                New
              </span>
            )}
          </span>
        </TabsTrigger>
      )}
      {payrollEmailsAccess !== PAYROLL_EMAILS_ACCESS.hidden && (
        <TabsTrigger
          value={PAY_BREAKDOWN_SECTION.packs}
          aria-label={
            payrollEmailsAccess === PAYROLL_EMAILS_ACCESS.preview
              ? "PayPacks, new"
              : undefined
          }
        >
          <span className="inline-flex items-center gap-1.5">
            PayPacks
            {payrollEmailsAccess === PAYROLL_EMAILS_ACCESS.preview && (
              <span className={newTabBadgeClassName} aria-hidden>
                New
              </span>
            )}
          </span>
        </TabsTrigger>
      )}
      {showPackChat && (
        <TabsTrigger value={PAY_BREAKDOWN_SECTION.packChat}>
          <span className="flex items-center gap-1.5">
            PackChat
            {/* The count is the point of the tab: without it a manager has to
                open it to find out whether anyone is waiting. */}
            {openThreadCount > 0 && (
              <AttentionCount
                count={openThreadCount}
                label={openThreadCount === 1 ? "open thread" : "open threads"}
              />
            )}
          </span>
        </TabsTrigger>
      )}
    </PageTabsList>
  </Tabs>
);
